import { NavLink } from 'react-router-dom';

import logo from '@/assets/light-nacon.png';
import { useGame } from '@/hooks/useGame';
import { sandboxCode } from '@/lib/utils';
import type { DashboardItem } from '@/models/dashboardItem';

import { GameImage } from '../gameMiniature';
import {
  Sidebar as ShadSidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '../ui/sidebar';

interface ProSidebarProps {
  items?: DashboardItem[];
}

const MenuItemWithChildren = ({ item }: { item: DashboardItem }) => {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild>
        <NavLink end to={item.link}>
          {item.icon} {item.label}
        </NavLink>
      </SidebarMenuButton>
      <SidebarMenuSub>
        {item.items?.map((_item) => (
          <SidebarMenuSubItem key={_item.label}>
            <SidebarMenuSubButton asChild>
              <NavLink
                end
                to={`${item.link}/${_item.link}`}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                {_item.icon} {_item.label}
              </NavLink>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        ))}
      </SidebarMenuSub>
    </SidebarMenuItem>
  );
};

export const AppSidebar = ({ items = [] }: ProSidebarProps) => {
  const game = useGame();
  const isNonProduction = import.meta.env.MODE !== 'production';
  return (
    <ShadSidebar>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <a
                href={`/game/${game.game_code}/${sandboxCode(game.game_code)}/players`}
                className="p-6 px-4"
              >
                <img
                  className="watermark"
                  src={logo}
                  width={24}
                  height={24}
                  alt="Nacon Live"
                />
                <span className="text-base">
                  Nacon Live{' '}
                  {isNonProduction && (
                    <span
                      className="data-[env=development]:text-cyan-300 data-[env=staging]:text-amber-300"
                      data-env={import.meta.env.MODE}
                    >
                      {import.meta.env.MODE === 'development'
                        ? 'Dev'
                        : 'Staging'}
                    </span>
                  )}
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          {items.map((item) => (
            <MenuItemWithChildren key={item.label} item={item} />
          ))}
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="p-4 rounded-md">
          <GameImage game={game} />
        </div>
      </SidebarFooter>
    </ShadSidebar>
  );
};
